/**
 * Create Table Modal
 * A new shared table: its name and its speed (how fast the ante rises —
 * the stakes are the same at every table). Creating it seats you there.
 */

import React, { useState } from 'react';
import { gameService } from '../../services/gameService';
import { showToast } from '../../lib/toast';
import { TABLE_SPEEDS, type TableSpeed } from './tableSpeeds';

interface Props {
  onClose: () => void;
  onCreated: (tableId: string) => void;
}

const CreateTableModal: React.FC<Props> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [speed, setSpeed] = useState<TableSpeed>('standard');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3) {
      setNameError('Name must be at least 3 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      // The speed sets the stakes on the server; the rest is what it requires.
      const table = await gameService.createTable({
        name: name.trim(),
        speed,
        maxPlayers: 10,
        minBuyIn: 1000,
        maxBuyIn: 1000,
        anteAmount: 100,
      });
      await gameService.joinTable(table.id, { buyInAmount: 1000 });
      onCreated(table.id);
    } catch (err: any) {
      showToast(err.message || 'Failed to create table', 'error');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-gray-800 rounded-2xl shadow-2xl border-2 border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Create Table</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[80vh]">
          {/* Name */}
          <div>
            <label htmlFor="table-name" className="block text-sm font-medium text-gray-300 mb-1">Table Name</label>
            <input
              id="table-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(null);
              }}
              placeholder="e.g. Friday Night"
              maxLength={50}
              className={`w-full px-3 py-2 bg-gray-900 border-2 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors ${
                nameError ? 'border-red-500' : 'border-gray-700'
              }`}
            />
            {nameError && <p className="mt-1 text-xs text-red-400">{nameError}</p>}
          </div>

          {/* Speed */}
          <fieldset>
            <legend className="block text-sm font-medium text-gray-300 mb-1">Speed</legend>
            <div className="space-y-2">
              {TABLE_SPEEDS.map((s) => (
                <label
                  key={s.key}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    speed === s.key ? 'border-yellow-500 bg-yellow-900/20' : 'border-gray-700 bg-gray-900 hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="speed"
                    value={s.key}
                    checked={speed === s.key}
                    onChange={() => setSpeed(s.key)}
                    className="accent-yellow-500"
                  />
                  <span className="flex-1">
                    <span className="block font-semibold text-white">{s.name}</span>
                    <span className="block text-xs text-gray-400">
                      {s.rise} · a game lasts {s.length}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Every table: 10 seats, 1,000 chips each, first ante 100. Bots fill the empty seats.
            </p>
          </fieldset>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors"
            >
              {isSubmitting ? 'Creating...' : 'Create & Sit Down'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTableModal;
